import React, { useState } from 'react';
import { useController, UseControllerProps } from 'react-hook-form';
import { Autocomplete, Checkbox, createFilterOptions, FormControl, MenuItem, TextField } from '@mui/material';
import { IDropDownOptions } from './AppSelectList';

interface IProps extends UseControllerProps {
    label: string;
    options: IDropDownOptions[];
    size?: "small" | "medium";
    disabled?: boolean;
    onChange?: (value: any) => void;
    limit?: number;
    readOnly?: boolean;
    freeSolo?: boolean;
    noUsersFound?: boolean;
}

const AppMultiSelectWithSelectAll = (props: IProps) => {
    const selectAllOption = { id: 'selectAll', value: 'Select All' };
    const allOptions = [selectAllOption, ...props.options];
    const { fieldState, field } = useController({ ...props });
    const [selectedOptions, setSelectedOptions] = useState(props.options.filter(option => (field.value || []).includes(option.id)));
    const [selectAllToggle, setSelectAll] = useState<boolean>(props.options.length === selectedOptions.length);

    const filterOptions: any = createFilterOptions<any>({
        matchFrom: 'start',
        stringify: (option) => option.value,
        limit: props.limit === undefined ? 20 : props.limit,
    });

    const handleSelectAllChange = () => {
        setSelectAll(prev => !prev);
        let selectedValues: any[];
        selectedValues = props.options.map((item: IDropDownOptions) => item.id)
        field.onChange(selectedValues);
        setSelectedOptions(props.options);

        if (selectAllToggle)
        {
            selectedValues = [];
            setSelectedOptions([]);
        }

        if (props.onChange) {
            props.onChange(selectedValues);
        }
    };

    return (
        <FormControl fullWidth error={!!fieldState.error}>
            <Autocomplete
                {...props}
                {...field}
                value={selectedOptions}
                onChange={(event, value) => {
                    const hasSelectAll = value.some(
                        (item: IDropDownOptions) => item.id === selectAllOption.id
                    );

                    if (hasSelectAll) {
                        handleSelectAllChange();
                    } else {
                        const values = value.map((item: IDropDownOptions) => item.id);
                        field.onChange(values);
                        if (props.onChange) {
                            if (values) {
                                props.onChange(values);
                                setSelectedOptions(value);
                            }
                        }
                        if (value.length === props.options.length) {
                            setSelectAll(true);
                        }
                        else {
                            setSelectAll(false);
                        }
                    }
                }}
                multiple
                disableCloseOnSelect
                options={allOptions}
                filterOptions={filterOptions}
                getOptionLabel={option => option.value}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => {
                    return (
                        <MenuItem key={option.id} value={option.id} {...props}>
                            {option.id === 'selectAll' &&
                                <Checkbox
                                    checked={selectAllToggle}
                                />
                            }
                            {option.id !== 'selectAll' &&
                                <Checkbox
                                    checked={selectedOptions.some((item: IDropDownOptions) => item.id === option.id)}
                                />
                            }
                            {option.value}
                        </MenuItem>
                    )
                }}
                renderInput={
                    (params) => <TextField
                        {...params}
                        fullWidth
                        label={props.label}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <React.Fragment>
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        }}
                        variant="outlined"
                        size={props.size}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                    />
                }
                disabled={props.noUsersFound}
            />
        </FormControl>)
}

export default AppMultiSelectWithSelectAll